import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { API } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';
import WebDaySelector from './WebDaySelector';
import WebTimeSlot from './WebTimeSlot';
import WebTimePicker from './WebTimePicker';
import { GamifyToast } from '../../../common/CustomToasts';

interface Schedule {
  id?: string;
  days: number[];
  startTime: number;
  endTime: number;
  minimumMeetingLength: number;
  maximumMeetingLength: number;
  defaultMeetingLength: number;
  minutesBetweenMeetings: number;
}

interface AggregatedSchedule extends Schedule {
  slots: Schedule[];
}

// Helper function to format time for error messages
const formatTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHours}:${mins.toString().padStart(2, '0')} ${ampm}`;
};

// Check for overlapping slots within the same day
const hasOverlappingSlots = (
  schedules: AggregatedSchedule[]
): {
  hasOverlap: boolean;
  firstOverlap?: { slot1: Schedule; slot2: Schedule };
} => {
  for (const sched of schedules) {
    for (let i = 0; i < sched.slots.length; i++) {
      for (let j = i + 1; j < sched.slots.length; j++) {
        const slot1 = sched.slots[i];
        const slot2 = sched.slots[j];

        // Check if slots overlap
        if (
          slot1.startTime < slot2.endTime &&
          slot2.startTime < slot1.endTime
        ) {
          return { hasOverlap: true, firstOverlap: { slot1, slot2 } };
        }
      }
    }
  }
  return { hasOverlap: false };
};

// Check for invalid time values
const hasInvalidTimes = (
  schedules: AggregatedSchedule[]
): { hasInvalid: boolean; firstInvalid?: Schedule } => {
  for (const sched of schedules) {
    for (const slot of sched.slots) {
      if (
        slot.startTime < 0 ||
        slot.endTime < 0 ||
        slot.startTime >= 1440 || // 24 hours = 1440 minutes
        slot.endTime > 1440 ||
        slot.startTime >= slot.endTime
      ) {
        return { hasInvalid: true, firstInvalid: slot };
      }
    }
  }
  return { hasInvalid: false };
};

// Check for unreasonable durations
const hasUnreasonableDurations = (
  schedules: AggregatedSchedule[]
): { hasUnreasonable: boolean; firstUnreasonable?: Schedule } => {
  for (const sched of schedules) {
    for (const slot of sched.slots) {
      const slotDuration = slot.endTime - slot.startTime;
      if (
        slotDuration < 30 || // Minimum 30 minutes
        slotDuration > 16 * 60 || // Maximum 16 hours
        slot.defaultMeetingLength > slotDuration
      ) {
        return { hasUnreasonable: true, firstUnreasonable: slot };
      }
    }
  }
  return { hasUnreasonable: false };
};

interface WebRecurringScheduleProps {
  userID: string;
  onSaveComplete: (success: boolean) => void;
}

const maintainPersonDefaultSchedule = `
  mutation MaintainPersonDefaultSchedule($request: AWSJSON!) {
    maintainPersonDefaultSchedule(request: $request)
  }
`;

const listPersonDefaultScheduleByUser = `
  query ListPersonDefaultScheduleByUser($userID: ID!) {
    listPersonDefaultScheduleByUser(userID: $userID) {
      items {
        days
        defaultMeetingLength
        endDate
        endTime
        maximumMeetingLength
        minimumMeetingLength
        minutesBetweenMeetings
        startTime
      }
    }
  }
`;

const defaultSchedule = [
  {
    days: [1, 2, 3, 4, 5], // Monday - Friday
    startTime: 12 * 60, // Noon
    endTime: 21 * 60, // 9PM
    minimumMeetingLength: 30,
    maximumMeetingLength: 600,
    defaultMeetingLength: 90,
    minutesBetweenMeetings: 0,
  },
  {
    days: [6], // Saturday
    startTime: 10 * 60, // 10AM
    endTime: 15 * 60, // 3PM
    minimumMeetingLength: 30,
    maximumMeetingLength: 600,
    defaultMeetingLength: 90,
    minutesBetweenMeetings: 0,
  },
];

const WebRecurringSchedule = forwardRef<unknown, WebRecurringScheduleProps>(
  (props, ref) => {
    const theme = useTheme();
    const [aggregatedSchedules, setAggregatedSchedules] = useState<
      AggregatedSchedule[]
    >([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pickTime, setPickTime] = useState<{
      slot: Schedule;
      isStartTime: boolean;
    } | null>(null);

    const roundToNearest15Minutes = (minutes: number): number => {
      return Math.round(minutes / 15) * 15;
    };

    const getInitialPickerTime = (slotTime: number): Date => {
      if (slotTime < 0) {
        const defaultDate = new Date();
        defaultDate.setHours(9, 0, 0, 0);
        return defaultDate;
      }

      const roundedTime = roundToNearest15Minutes(slotTime);
      const hours = Math.floor(roundedTime / 60);
      const minutes = roundedTime % 60;
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      return date;
    };

    const minutesPastMidnight = (timeString: Date): number => {
      const hours = timeString.getHours();
      const minutes = timeString.getMinutes();
      return hours * 60 + minutes;
    };

    const getDefaultScheduleConfig = () => {
      // @ts-ignore
      const globalSettings = global?.appSettings?.defaultSchedule?.default?.[0];
      if (globalSettings) {
        return {
          days: globalSettings.days || [1, 2, 3, 4, 5],
          defaultMeetingLength: globalSettings.defaultMeetingLength || 90,
          endTime: globalSettings.endTime || 1260,
          maximumMeetingLength: globalSettings.maximumMeetingLength || 600,
          minimumMeetingLength: globalSettings.minimumMeetingLength || 30,
          minutesBetweenMeetings: globalSettings.minutesBetweenMeetings || 0,
          startTime: globalSettings.startTime || 720,
        };
      }

      return {
        defaultMeetingLength: 90,
        endTime: 1260,
        maximumMeetingLength: 600,
        minimumMeetingLength: 30,
        minutesBetweenMeetings: 0,
        startTime: 720,
      };
    };

    const fetchDefaultSchedule = async (userID: string) => {
      try {
        const res = await API.graphql({
          query: listPersonDefaultScheduleByUser,
          variables: { userID },
        });

        // @ts-ignore
        let schedule = [getDefaultScheduleConfig()];
        // @ts-ignore
        if (res.data.listPersonDefaultScheduleByUser.items.length > 0) {
          schedule = [];
          // @ts-ignore
          for (const item of res.data.listPersonDefaultScheduleByUser.items) {
            // Convert from Python days to Javascript days
            // Python: 0=Monday, 6=Sunday
            // Javascript: 0=Sunday, 6=Saturday
            const days = [];
            for (const day of item.days) {
              days.push(day === 6 ? 0 : day + 1);
            }
            item.days = days;
            schedule.push(item);
          }
        }
        return schedule;
      } catch (error) {
        console.error('Error fetching schedule:', error);
        return defaultSchedule;
      }
    };

    const onAggregateSchedules = (newSchedule: any[]): AggregatedSchedule[] => {
      if (newSchedule.length) {
        const hasSlotsProperty = newSchedule.some((schedule) =>
          Object.prototype.hasOwnProperty.call(schedule, 'slots')
        );

        if (hasSlotsProperty) {
          const aggregatedSchedules = Object.values(
            newSchedule.reduce((result: any, obj: any) => {
              const key = JSON.stringify(
                {
                  startTime: obj.startTime,
                  endTime: obj.endTime,
                  minimumMeetingLength: obj.minimumMeetingLength,
                  maximumMeetingLength: obj.maximumMeetingLength,
                  defaultMeetingLength: obj.defaultMeetingLength,
                  minutesBetweenMeetings: obj.minutesBetweenMeetings,
                },
                Object.keys(obj).sort()
              );
              if (!result[key]) {
                result[key] = {
                  days: [...obj.days],
                  slots: [{ ...obj, id: uuidv4() }],
                };
              } else {
                result[key].days.push(...obj.days);
              }
              return result;
            }, {})
          );

          return aggregatedSchedules.map((schedule: any) => ({
            days: Array.from(
              new Set(schedule.days as number[])
            ).sort() as number[],
            slots: schedule.slots,
            startTime: schedule.slots[0]?.startTime || 0,
            endTime: schedule.slots[0]?.endTime || 0,
            minimumMeetingLength: schedule.slots[0]?.minimumMeetingLength || 30,
            maximumMeetingLength:
              schedule.slots[0]?.maximumMeetingLength || 600,
            defaultMeetingLength: schedule.slots[0]?.defaultMeetingLength || 90,
            minutesBetweenMeetings:
              schedule.slots[0]?.minutesBetweenMeetings || 0,
          }));
        } else {
          const aggregatedSchedules = Object.values(
            newSchedule.reduce((result: any, obj: any) => {
              const key = JSON.stringify(
                {
                  startTime: obj.startTime,
                  endTime: obj.endTime,
                  minimumMeetingLength: obj.minimumMeetingLength,
                  maximumMeetingLength: obj.maximumMeetingLength,
                  defaultMeetingLength: obj.defaultMeetingLength,
                  minutesBetweenMeetings: obj.minutesBetweenMeetings,
                },
                Object.keys(obj).sort()
              );
              if (!result[key]) {
                result[key] = {
                  days: [...obj.days],
                  slots: [{ ...obj, id: uuidv4() }],
                };
              } else {
                result[key].days.push(...obj.days);
              }
              return result;
            }, {})
          );

          const reducedSchedules = aggregatedSchedules.reduce(
            (result: Record<string, AggregatedSchedule>, schedule: any) => {
              const key = JSON.stringify(schedule.days);
              if (!result[key]) {
                result[key] = {
                  days: schedule.days,
                  slots: [{ ...schedule.slots[0], id: uuidv4() }],
                  startTime: schedule.slots[0]?.startTime || 0,
                  endTime: schedule.slots[0]?.endTime || 0,
                  minimumMeetingLength:
                    schedule.slots[0]?.minimumMeetingLength || 30,
                  maximumMeetingLength:
                    schedule.slots[0]?.maximumMeetingLength || 600,
                  defaultMeetingLength:
                    schedule.slots[0]?.defaultMeetingLength || 90,
                  minutesBetweenMeetings:
                    schedule.slots[0]?.minutesBetweenMeetings || 0,
                };
              } else {
                result[key].slots.push({ ...schedule.slots[0], id: uuidv4() });
              }
              return result;
            },
            {} as Record<string, AggregatedSchedule>
          );

          return Object.values(reducedSchedules);
        }
      }
      return [];
    };

    const loadDefaultSchedule = async () => {
      setIsLoading(true);
      try {
        const currentSchedule = await fetchDefaultSchedule(props.userID);
        const updatedSchedule = onAggregateSchedules(currentSchedule);
        setAggregatedSchedules(updatedSchedule);
      } catch (error) {
        console.error('Error loading schedule:', error);
      } finally {
        setIsLoading(false);
      }
    };

    useEffect(() => {
      loadDefaultSchedule();
    }, [props.userID]);

    const prepareScheduleForSubmission = (
      aggregatedData: AggregatedSchedule[]
    ) => {
      const scheduleForSubmission: any = {};

      for (const schedule of aggregatedData) {
        const { days, slots } = schedule;

        for (const day of days) {
          if (!scheduleForSubmission[day]) {
            scheduleForSubmission[day] = { slots: [] };
          }

          for (const slot of slots) {
            const { id, ...slotWithoutId } = slot;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _unused = id;
            const existingSlot = scheduleForSubmission[day].slots.find(
              (s: any) =>
                s.startTime === slotWithoutId.startTime &&
                s.endTime === slotWithoutId.endTime &&
                s.minimumMeetingLength === slotWithoutId.minimumMeetingLength &&
                s.maximumMeetingLength === slotWithoutId.maximumMeetingLength &&
                s.defaultMeetingLength === slotWithoutId.defaultMeetingLength &&
                s.minutesBetweenMeetings ===
                  slotWithoutId.minutesBetweenMeetings
            );

            if (!existingSlot) {
              scheduleForSubmission[day].slots.push({
                days: [day],
                startTime: slotWithoutId.startTime,
                endTime: slotWithoutId.endTime,
                minimumMeetingLength: slotWithoutId.minimumMeetingLength,
                maximumMeetingLength: slotWithoutId.maximumMeetingLength,
                defaultMeetingLength: slotWithoutId.defaultMeetingLength,
                minutesBetweenMeetings: slotWithoutId.minutesBetweenMeetings,
              });
            } else {
              existingSlot.days.push(day);
            }
          }
        }
      }

      return scheduleForSubmission;
    };

    const onSave = async () => {
      try {
        const formattedSchedule =
          prepareScheduleForSubmission(aggregatedSchedules);

        // Comprehensive validation with enhanced error messages
        if (!aggregatedSchedules.length) {
          GamifyToast.error("Schedule can't be empty");
          return;
        }

        const hasTimeSlots = aggregatedSchedules.some(
          (sched) => sched.slots.length > 0
        );
        if (!hasTimeSlots) {
          GamifyToast.error('Please add at least one time slot');
          return;
        }

        // Check for invalid time values
        const invalidTimesCheck = hasInvalidTimes(aggregatedSchedules);
        if (invalidTimesCheck.hasInvalid && invalidTimesCheck.firstInvalid) {
          const slot = invalidTimesCheck.firstInvalid;
          GamifyToast.error(
            `Invalid time detected: ${
              slot.startTime >= 0 ? formatTime(slot.startTime) : 'Invalid start'
            } - ${slot.endTime >= 0 ? formatTime(slot.endTime) : 'Invalid end'}`
          );
          return;
        }

        // Check for overlapping slots
        const overlapCheck = hasOverlappingSlots(aggregatedSchedules);
        if (overlapCheck.hasOverlap && overlapCheck.firstOverlap) {
          const { slot1, slot2 } = overlapCheck.firstOverlap;
          GamifyToast.error(
            `Time slots overlap: ${formatTime(slot1.startTime)}-${formatTime(
              slot1.endTime
            )} overlaps with ${formatTime(slot2.startTime)}-${formatTime(
              slot2.endTime
            )}`
          );
          return;
        }

        // Check for unreasonable durations
        const unreasonableCheck = hasUnreasonableDurations(aggregatedSchedules);
        if (
          unreasonableCheck.hasUnreasonable &&
          unreasonableCheck.firstUnreasonable
        ) {
          const slot = unreasonableCheck.firstUnreasonable;
          const duration = slot.endTime - slot.startTime;
          GamifyToast.error(
            `Invalid slot duration: ${formatTime(slot.startTime)}-${formatTime(
              slot.endTime
            )} (${Math.floor(duration / 60)}h ${duration % 60}m) is too ${
              duration < 30 ? 'short' : 'long'
            }`
          );
          return;
        }

        // Check if meeting duration fits in slot
        const hasInvalidSlots = aggregatedSchedules.some((sched) =>
          sched.slots.some(
            (slot) => slot.endTime - slot.startTime < slot.defaultMeetingLength
          )
        );

        if (hasInvalidSlots) {
          // Find the first invalid slot for better error message
          let invalidSlot: Schedule | null = null;
          for (const sched of aggregatedSchedules) {
            for (const slot of sched.slots) {
              if (slot.endTime - slot.startTime < slot.defaultMeetingLength) {
                invalidSlot = slot;
                break;
              }
            }
            if (invalidSlot) break;
          }

          if (invalidSlot) {
            GamifyToast.error(
              `Meeting duration too long: ${formatTime(
                invalidSlot.startTime
              )}-${formatTime(invalidSlot.endTime)} slot is shorter than ${
                invalidSlot.defaultMeetingLength
              } min meeting`
            );
          } else {
            GamifyToast.error(
              'Please ensure meeting duration fits within time slots'
            );
          }
          return;
        }

        const newSchedule = JSON.parse(JSON.stringify(formattedSchedule));
        newSchedule.userID = props.userID;

        await API.graphql({
          query: maintainPersonDefaultSchedule,
          variables: {
            request: JSON.stringify(newSchedule),
          },
        });

        GamifyToast.success('Schedule saved');
        props.onSaveComplete(true);
      } catch (error) {
        console.error('Error saving schedule:', error);
        GamifyToast.error('There was a problem saving the schedule');
        props.onSaveComplete(false);
      }
    };

    useImperativeHandle(ref, () => ({
      save() {
        onSave();
      },
    }));

    const updateScheduleDays = (schedIndex: number, selectedDays: number[]) => {
      const updatedSchedules = [...aggregatedSchedules];
      const currentSchedule = updatedSchedules[schedIndex];

      if (selectedDays.length === 0) {
        return;
      }

      const updatedSchedule = {
        ...currentSchedule,
        days: selectedDays,
        slots: currentSchedule.slots.map((slot) => ({
          ...slot,
          days: selectedDays,
        })),
      };

      updatedSchedules[schedIndex] = updatedSchedule;
      setAggregatedSchedules(updatedSchedules);
    };

    const onCancelDays = (schedIndex: number) => {
      const updatedSchedules = [...aggregatedSchedules];
      updatedSchedules.splice(schedIndex, 1);
      setAggregatedSchedules(updatedSchedules);
    };

    const handleTimePress = (slot: Schedule, isStartTime: boolean) => {
      console.log('Time press:', { slot, isStartTime });
      setPickTime({ slot, isStartTime });
    };

    const updateTimeValue = (
      slotToUpdate: Schedule,
      isStartTime: boolean,
      time: Date
    ) => {
      const timeType = isStartTime ? 'startTime' : 'endTime';
      const updatedSchedule = aggregatedSchedules.map((schedule) => {
        const updatedSlots = schedule.slots.map((slot) => {
          if (slot.id === slotToUpdate.id) {
            const newTimePastMidnight = minutesPastMidnight(time);

            if (timeType === 'startTime') {
              if (slot.endTime > 0 && newTimePastMidnight > slot.endTime) {
                GamifyToast.error('The start time must be < the end time.');
                return slot;
              } else {
                return {
                  ...slot,
                  startTime: newTimePastMidnight,
                };
              }
            } else if (timeType === 'endTime') {
              if (slot.startTime > 0 && newTimePastMidnight < slot.startTime) {
                GamifyToast.error('The end time must be > the start time');
                return slot;
              } else {
                return {
                  ...slot,
                  endTime: newTimePastMidnight,
                };
              }
            }
          }
          return slot;
        });

        return {
          ...schedule,
          slots: updatedSlots,
        };
      });

      setAggregatedSchedules(updatedSchedule);
    };

    const handleTimePickerConfirm = (time: Date) => {
      console.log('Time picker confirm:', { time, pickTime });
      if (pickTime) {
        updateTimeValue(pickTime.slot, pickTime.isStartTime, time);
        setPickTime(null);
      }
    };

    const onChangeDuration = (slot: Schedule, newDuration: string) => {
      setAggregatedSchedules((prevSchedules) =>
        prevSchedules.map((schedule) => ({
          ...schedule,
          slots: schedule.slots.map((s) =>
            s.id === slot.id
              ? { ...s, defaultMeetingLength: parseInt(newDuration) }
              : s
          ),
        }))
      );
    };

    const onAddRecurringDay = () => {
      const allDays = [0, 1, 2, 3, 4, 5, 6];
      const existingDays = aggregatedSchedules.reduce(
        (days: number[], schedule) => {
          return [...days, ...schedule.days];
        },
        []
      );
      const uniqueExistingDays = Array.from(new Set(existingDays));
      const missingDays = allDays.filter(
        (day) => !uniqueExistingDays.includes(day)
      );

      if (missingDays.length === 0) {
        return;
      }

      const defaultConfig = getDefaultScheduleConfig();

      const newSchedule: AggregatedSchedule = {
        days: missingDays,
        startTime: defaultConfig.startTime,
        endTime: defaultConfig.endTime,
        minimumMeetingLength: defaultConfig.minimumMeetingLength,
        maximumMeetingLength: defaultConfig.maximumMeetingLength,
        defaultMeetingLength: defaultConfig.defaultMeetingLength,
        minutesBetweenMeetings: defaultConfig.minutesBetweenMeetings,
        slots: [
          {
            id: uuidv4(),
            days: missingDays,
            defaultMeetingLength: defaultConfig.defaultMeetingLength,
            endTime: defaultConfig.endTime,
            maximumMeetingLength: defaultConfig.maximumMeetingLength,
            minimumMeetingLength: defaultConfig.minimumMeetingLength,
            minutesBetweenMeetings: defaultConfig.minutesBetweenMeetings,
            startTime: defaultConfig.startTime,
          },
        ],
      };

      const updatedSchedule = [...aggregatedSchedules, newSchedule];
      setAggregatedSchedules(updatedSchedule);
    };

    const shouldShowAddButton = () => {
      if (!aggregatedSchedules) {
        return true;
      }

      const allDays = [0, 1, 2, 3, 4, 5, 6];
      const existingDays = aggregatedSchedules.reduce(
        (days: number[], schedule) => {
          return [...days, ...schedule.days];
        },
        []
      );
      const uniqueExistingDays = Array.from(new Set(existingDays));

      return uniqueExistingDays.length !== allDays.length;
    };

    const onRemoveTimeSlot = (slotToRemove: Schedule) => {
      const updatedAggregatedData = aggregatedSchedules
        .map((schedule) => {
          const updatedSlots = schedule.slots.filter(
            (slot) => slot.id !== slotToRemove.id
          );
          return updatedSlots.length > 0
            ? { ...schedule, slots: updatedSlots }
            : null;
        })
        .filter(
          (schedule): schedule is AggregatedSchedule => schedule !== null
        );

      setAggregatedSchedules(updatedAggregatedData);
    };

    const onAddSlot = (scheduleIndex: number) => {
      const defaultConfig = getDefaultScheduleConfig();
      const sched = aggregatedSchedules[scheduleIndex];

      const newSlot: Schedule = {
        id: uuidv4(),
        days: sched.days,
        defaultMeetingLength: defaultConfig.defaultMeetingLength,
        endTime: -1,
        maximumMeetingLength: defaultConfig.maximumMeetingLength,
        minimumMeetingLength: defaultConfig.minimumMeetingLength,
        minutesBetweenMeetings: defaultConfig.minutesBetweenMeetings,
        startTime: -1,
      };

      const updatedSchedules = [...aggregatedSchedules];
      updatedSchedules[scheduleIndex] = {
        ...sched,
        slots: [...sched.slots, newSlot],
      };

      setAggregatedSchedules(updatedSchedules);
    };

    if (isLoading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
          bgcolor={theme.custom.background1}
        >
          <CircularProgress size={40} sx={{ color: theme.custom.text }} />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: theme.custom.background1,
        }}
      >
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {aggregatedSchedules.map((sched, index) => {
            if (!sched.days.length || !sched.slots.length) {
              return null;
            }

            return (
              <Box
                key={sched.days.join(',')}
                sx={{
                  borderBottom: `1px solid ${theme.custom.stroke}`,
                  mb: 2,
                  pb: 2,
                }}
              >
                {index !== 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: theme.custom.text }}
                    >
                      Extra dates
                    </Typography>
                    <Button
                      onClick={() => onCancelDays(index)}
                      sx={{
                        color: theme.custom.primary,
                        textTransform: 'none',
                        fontWeight: 600,
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}

                <Box sx={{ mb: 2 }}>
                  <WebDaySelector
                    onToggle={(selectedDays: number[]) => {
                      updateScheduleDays(index, selectedDays);
                    }}
                    isFirst={index === 0}
                    initialValue={sched.days}
                  />

                  {sched.slots.map((slot, slotIndex) => (
                    <WebTimeSlot
                      key={slot.id || slotIndex}
                      slot={slot}
                      onTimePress={handleTimePress}
                      onAdd={() => onAddSlot(index)}
                      onRemove={() => onRemoveTimeSlot(slot)}
                      onChangeDuration={(newDuration: string) =>
                        onChangeDuration(slot, newDuration)
                      }
                    />
                  ))}
                </Box>
              </Box>
            );
          })}

          {shouldShowAddButton() && (
            <Button
              onClick={onAddRecurringDay}
              sx={{
                color: theme.custom.primary,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              + Add another day
            </Button>
          )}
        </Box>

        {pickTime && (
          <WebTimePicker
            open={!!pickTime}
            initialTime={getInitialPickerTime(
              pickTime.slot[pickTime.isStartTime ? 'startTime' : 'endTime']
            )}
            onConfirm={handleTimePickerConfirm}
            onCancel={() => setPickTime(null)}
          />
        )}
      </Box>
    );
  }
);

WebRecurringSchedule.displayName = 'WebRecurringSchedule';

export default WebRecurringSchedule;
